import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, FilePlus2, Sparkles, Receipt, Menu, X, Bell } from "lucide-react";

/**
 * ProgereMobileTabBar — Bottom navigation for mobile PWA / app installs.
 * Visible only on mobile (< lg breakpoint). Auto-hidden on desktop.
 * Respects iOS safe-area-inset-bottom for full-screen PWA.
 */
export default function ProgereMobileTabBar({ onMenuClick, unread = 0 }) {
  const loc = useLocation();
  const [visible, setVisible] = useState(true);
  const [lastY, setLastY] = useState(0);

  // Hide tabbar on scroll down, show on scroll up (native app feel)
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 40) { setVisible(true); setLastY(y); return; }
      if (Math.abs(y - lastY) < 12) return;
      setVisible(y < lastY);
      setLastY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastY]);

  const items = [
    { to: "/app", icon: LayoutDashboard, label: "Accueil", testid: "tab-home", exact: true },
    { to: "/app/quotes/new", icon: FilePlus2, label: "Nouveau", testid: "tab-new", accent: true },
    { to: "/app/quotes/new/vision", icon: Sparkles, label: "Vision", testid: "tab-vision" },
    { to: "/app/invoices", icon: Receipt, label: "Factures", testid: "tab-invoices" },
  ];

  const activeStyle = { color: "var(--pg-gold)" };
  const inactiveStyle = { color: "rgba(245,245,245,0.55)" };

  return (
    <nav
      data-testid="mobile-tabbar"
      aria-label="Navigation mobile"
      className="lg:hidden fixed left-0 right-0 z-40 transition-transform"
      style={{
        bottom: 0,
        transform: visible ? "translateY(0)" : "translateY(calc(100% + 20px))",
        background: "rgba(11,12,15,0.92)",
        backdropFilter: "blur(24px) saturate(150%)",
        WebkitBackdropFilter: "blur(24px) saturate(150%)",
        borderTop: "1px solid rgba(212,175,55,0.15)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -8px 32px rgba(0,0,0,0.4)",
      }}
    >
      <div className="grid grid-cols-5 items-stretch">
        {items.slice(0, 2).map((it) => (
          <TabItem key={it.to} item={it} loc={loc} activeStyle={activeStyle} inactiveStyle={inactiveStyle} />
        ))}
        {/* Center accent — PROGÈRE hex logo tap zone */}
        <div className="flex items-center justify-center py-1">
          <NavLink to="/app/quotes/new/vision" data-testid="tab-center-vision"
            className="relative flex items-center justify-center w-14 h-14 rounded-full transition active:scale-95"
            style={{
              background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)",
              boxShadow: "0 6px 18px rgba(212,175,55,0.5), 0 0 0 3px rgba(11,12,15,0.9)",
              marginTop: "-22px",
            }}>
            <Sparkles size={22} color="#0B0B0B" strokeWidth={2.5} />
          </NavLink>
        </div>
        {items.slice(2).map((it) => (
          <TabItem key={it.to} item={it} loc={loc} activeStyle={activeStyle} inactiveStyle={inactiveStyle} />
        ))}
        <button onClick={onMenuClick} data-testid="tab-more"
          className="flex flex-col items-center justify-center gap-0.5 py-2 relative"
          style={inactiveStyle} aria-label="Menu complet">
          <Menu size={18} />
          <span className="text-[10px] font-semibold">Menu</span>
          {unread > 0 && (
            <span className="absolute top-1 right-1/4 min-w-[16px] h-[16px] rounded-full text-[9px] flex items-center justify-center font-bold px-1"
              style={{ background: "#C00000", color: "#fff" }} data-testid="tab-more-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </div>
    </nav>
  );
}

const TabItem = ({ item, loc, activeStyle, inactiveStyle }) => {
  const Icon = item.icon;
  const isActive = item.exact ? loc.pathname === item.to : loc.pathname === item.to || loc.pathname.startsWith(item.to + "/");
  return (
    <NavLink to={item.to} data-testid={item.testid}
      className="flex flex-col items-center justify-center gap-0.5 py-2 transition active:scale-95"
      style={isActive ? activeStyle : inactiveStyle}>
      <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
      <span className="text-[10px] font-semibold">{item.label}</span>
    </NavLink>
  );
};

/**
 * ProgereInstallPWABanner — Shows "Ajouter à l'écran d'accueil" prompt when the
 * browser fires `beforeinstallprompt`. Also detects iOS Safari and shows a hint.
 */
export function ProgereInstallPWABanner() {
  const [prompt, setPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("progere_pwa_dismissed") === "1"; } catch { return false; }
  });
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect if already running as PWA
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);
    // iOS detection (Safari doesn't fire beforeinstallprompt)
    const ua = window.navigator.userAgent;
    const iosLike = /iP(hone|ad|od)/.test(ua) && !window.MSStream;
    setIsIos(iosLike);
    // Capture install prompt if browser already fired it before mount
    if (window.__progereInstallPrompt) setPrompt(window.__progereInstallPrompt);
    const onReady = () => setPrompt(window.__progereInstallPrompt);
    const onInstalled = () => { setPrompt(null); setDismissed(true); };
    window.addEventListener("progere:pwa-installable", onReady);
    window.addEventListener("progere:pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("progere:pwa-installable", onReady);
      window.removeEventListener("progere:pwa-installed", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("progere_pwa_dismissed", "1"); } catch { /* ignore */ }
  };

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    try { await prompt.userChoice; } catch { /* ignore */ }
    setPrompt(null);
  };

  if (isStandalone || dismissed) return null;
  if (!prompt && !isIos) return null;

  return (
    <div className="lg:hidden fixed left-3 right-3 z-50 rounded-2xl p-3 flex items-center gap-3"
      data-testid="pwa-install-banner"
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 12px)",
        background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(192,0,0,0.12))",
        border: "1px solid rgba(212,175,55,0.45)",
        backdropFilter: "blur(24px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
      }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)" }}>
        <span className="font-black text-[11px]" style={{ color: "#0B0B0B" }}>PGR</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold" style={{ color: "#F5F5F5" }}>
          Installer PROGÈRE
        </div>
        <div className="text-[10px] leading-tight" style={{ color: "rgba(245,245,245,0.7)" }}>
          {isIos ? "Partager → Ajouter à l'écran d'accueil" : "Utilisez PROGÈRE comme une vraie app"}
        </div>
      </div>
      {prompt && (
        <button onClick={install} data-testid="pwa-install-btn"
          className="text-xs font-bold px-3 py-1.5 rounded-full"
          style={{ background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)", color: "#0B0B0B" }}>
          Installer
        </button>
      )}
      <button onClick={dismiss} data-testid="pwa-install-dismiss"
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "rgba(255,255,255,0.06)" }} aria-label="Fermer">
        <X size={13} color="rgba(245,245,245,0.6)" />
      </button>
    </div>
  );
}
